import { createSelector } from '@ngrx/store';
import { FieldState, FilterState, OptionState, OptionStateWithCount } from '../../../../../models';
import { FilterInstanceHelper } from '../../../../utils';
import { createFilterStateSelector } from '../create-filter-state-selector/create-filter-state.selector';
import { SearchSelectors } from '../../search';
import { findIntersectionsBetweenArrays } from '@anecdotes/utils';
import { createFieldRelatedOptionsSelector } from '../create-field-related-options/create-field-related-options.selector';

export function calculateCountForOption(
  filterState: FilterState,
  searchFilteredDataIDs: string[],
  fieldId: string,
  optionId: string
): number {
  if (!filterState || !filterState.fieldsState) {
    return 0;
  }

  const currentField: FieldState = filterState.fieldsState[fieldId];
  const currentOption: OptionState = currentField.options[optionId];

  if (currentOption?.relatedDataInfo && !Object.keys(currentOption.relatedDataInfo).length) {
    return 0;
  }

  const otherFields = Object.values(filterState.fieldsState).filter((field) => field.id !== fieldId);
  const otherFieldsWithSelectedOptions = otherFields
    .map((field) => Object.values(field.options).filter((option) => option.isChecked))
    .filter((options) => options.length);

  const reversedOptions = otherFields
    .map((field) => Object.values(field.options).filter((option) => option.reverseFilter && !option.isChecked))
    .filter((options) => options.length);

  let filteredParentDataIds = FilterInstanceHelper.Instance.calculateFilteredDataIdsForOptionsGroups(
    [...otherFieldsWithSelectedOptions, [currentOption]],
    filterState.dataToFilter
  );

  if (searchFilteredDataIDs) {
    filteredParentDataIds = findIntersectionsBetweenArrays([filteredParentDataIds, searchFilteredDataIDs]);
  }

  if (reversedOptions.length) {
    const reversed = new Set<string>(
      FilterInstanceHelper.Instance.calculateFilteredDataIdsForOptionsGroups(
        [...reversedOptions, [currentOption]],
        filterState.dataToFilter
      )
    );

    filteredParentDataIds = filteredParentDataIds.filter((id) => !reversed.has(id));
  }

  if (currentField.shouldNestedBeCounted) {
    if (currentField.nestedGroupId) {
      return FilterInstanceHelper.Instance.calculateCountForOptionInNestedGroup(
        fieldId,
        optionId,
        filteredParentDataIds,
        filterState
      );
    }

    return FilterInstanceHelper.Instance.calculateCountForNestedOption(fieldId, optionId, filterState);
  }

  return filteredParentDataIds.length;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const createOptionsWithCountForFieldSelector = (filterInstanceId: string, fieldId: string) =>
  createSelector(
    createFilterStateSelector(filterInstanceId),
    createFieldRelatedOptionsSelector(filterInstanceId, fieldId),
    SearchSelectors.createSearchFilteredDataIdsSelector(filterInstanceId),
    (filterState, fieldRelatedOptions, searchFilteredDataIDs) => {
      if (!filterState) {
        return [];
      }

      const result: OptionStateWithCount[] = [];

      fieldRelatedOptions.forEach((option) => {
        result.push({
          ...option,
          count: calculateCountForOption(filterState, searchFilteredDataIDs, fieldId, option.id),
        });
      });

      return result;
    }
  );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const createOptionCountCalculationSelector = (filterInstanceId: string, fieldId: string, optionId: string) =>
  createSelector(
    createFilterStateSelector(filterInstanceId),
    SearchSelectors.createSearchFilteredDataIdsSelector(filterInstanceId),
    (filterState, searchFilteredDataIDs) =>
      calculateCountForOption(filterState, searchFilteredDataIDs, fieldId, optionId)
  );
